import type { ProcedureUtils } from "@orpc/tanstack-query";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";

import { orpc } from "@/client";
import { notifyError } from "@/lib/error-code";
import { ExamLabels } from "@/lib/labels/exams";

function useInvalidate() {
	const queryClient = useQueryClient();

	return () => {
		void queryClient.invalidateQueries({ queryKey: orpc.exams.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.medico.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.clinica.key() });
	};
}

const onError = notifyError(ExamLabels.error);

function useExamMutation<TInput, TOutput, TError>(
	procedure: ProcedureUtils<Record<never, never>, TInput, TOutput, TError>,
) {
	const invalidate = useInvalidate();

	return useMutation(procedure.mutationOptions({ onSuccess: invalidate, onError }));
}

export function useExamsByPatient(patientId: number) {
	return useSuspenseQuery(orpc.exams.byPatient.queryOptions({ input: { patient_id: patientId } }));
}

export function useExamSuggested(page: number) {
	return useQuery(
		orpc.exams.queue.queryOptions({
			input: { page },
			placeholderData: keepPreviousData,
		}),
	);
}

export function useExamsWithoutOwner() {
	return useQuery(orpc.exams.withoutOwner.queryOptions());
}

export const useExamUpload = () => useExamMutation(orpc.exams.upload);

export const useExamConfirm = () => useExamMutation(orpc.exams.confirm);

export const useExamDiscard = () => useExamMutation(orpc.exams.discard);

export const useExamAnchor = () => useExamMutation(orpc.exams.anchor);

export const useExamEditResult = () => useExamMutation(orpc.exams.editResult);

export const useExamArbitrateResult = () => useExamMutation(orpc.exams.arbitrateResult);

export const useExamArbitrateDate = () => useExamMutation(orpc.exams.arbitrateDate);

export const useExamConfirmIdentity = () => useExamMutation(orpc.exams.confirmIdentity);

export const useExamReanalyze = () => useExamMutation(orpc.exams.reanalyze);

export const useExamEditInterpretation = () => useExamMutation(orpc.exams.editInterpretation);
